import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { CreateCompany, useGetCompanyParents } from "../../api/CompanyApi";
import FormBase from "../../components/FormComponents/FormBase";
import GenericInput from "../../components/FormComponents/GenericInput";
import ModalForm from "../../components/FormComponents/ModalForm";
import When from "../../components/When";
import { CompanyType, Role } from "../../core/enums/enums";
import { createNewCompany, validateOrgNo } from "../../core/helpers/companyHelper";
import { toastSuccess } from "../../core/helpers/toastHelper";
import { ICompany, ICreateCompany } from "../../core/interfaces/company";
import { AppQueryKey } from "../../core/queryKeys";
import useAccessCheck from "../../hooks/useAccessCheck";
import { useGetUser } from "../../hooks/useAuth";
import useUpdatableObject from "../../hooks/useUpdatableObject";
import { addToast } from "../../store/slices/uiSlice";


const Hr: React.FC<{ title: string, children: JSX.Element | JSX.Element[] }> = ({ title, children }) => {
    return <><Row>
        <Col sm={1}>
            <b>{title}</b>
        </Col>
        <Col sm={11}>
            <hr />
        </Col>
    </Row>
        <Row style={{ marginLeft: 1 }}>
            {children}
        </Row>
    </>
}

const NewCompanyModal: React.FC<{ show: boolean, hide: () => void }> = ({ show, hide }) => {
    const dispatch = useDispatch();
    const useQuery = useQueryClient()
    const [, checkRole] = useAccessCheck();
    const user = useGetUser();
    const [updated, setUpdated, isEdited, submit, loading] = useUpdatableObject<ICreateCompany, ICompany>(createNewCompany(user.parentCompanyId), CreateCompany, {
        getErrorToast: (status: number): { title: string, message: string } => {
            const info = { title: 'Ett fel inträffade', message: 'Var vänligen försök igen om en liten stund.' }
            if (status === 409) {
                info.message = "Det finns redan ett företag med det angivna organisationsnumret."
            }
            return info;
        },
    });
    const [errors, setErrors] = useState({
        orgNo: true,
        name: true,
        city: true
    });

    useEffect(() => {
        if (show) {
            setUpdated(createNewCompany(user.parentCompanyId));
        }
    }, [show, setUpdated])

    const validate = (company: ICreateCompany, name: string) => {
        switch (name) {
            case 'name':
                errors.name = !company.name.length;
                break;
            case 'orgNo':
                if (company.orgNo.length > 6 && !company.orgNo.includes("-")) {
                    company.orgNo = company.orgNo.slice(0, 6) + '-' + company.orgNo.slice(6);
                }
                errors.orgNo = !validateOrgNo(company.orgNo);
                break;
            case 'city':
                errors.city = !company.city.length;
                break;
        }

        setErrors(errors)
        return company;
    }

    const handleSubmit = () => {
        submit().then(company => {
            hide();
            useQuery.invalidateQueries([AppQueryKey.COMPANIES]);
            useQuery.invalidateQueries([AppQueryKey.COMPANY_PARENTS]);          
            dispatch(addToast(toastSuccess("Det lyckades", company.name + " har skapats.")));
        })
    }

    const [companyParents] = useGetCompanyParents();

    const commonProps = {
        isEditMode: true,
        object: updated,
        errorObject: errors,
        setValue: setUpdated,
        onChangeMiddleWare: validate,
    }
    return (
        <ModalForm size={'lg'} show={show} hide={hide} title={"Skapa nytt företag"} isEdited={isEdited} submit={handleSubmit}
            isLoading={loading} disableSubmit={!!Object.values(errors).filter(s => s).length}>
            <Hr title={"Företagsinfo"}>
                <Row>
                    <Col md={6}>
                        <FormBase row={false} controlId={'companyName'} title={'Företagsnamn'}
                            className={'pb-2'}>
                            <GenericInput name={'name'} {...commonProps} />
                        </FormBase>
                    </Col>
                    <Col md={6}>
                        <FormBase row={false} controlId={'orgNo'} title={'Organisationsnummer'}
                            className={'pb-2'}>
                            <GenericInput name={'orgNo'} {...commonProps} description={"I formatet xxxxxx-xxxx"} />
                        </FormBase>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <FormBase row={false} controlId={'city'} title={'Stad'}
                            className={'pb-2'}>
                            <GenericInput name={'city'} {...commonProps} />
                        </FormBase>
                    </Col>
                    <Col md={6}>
                        <FormBase row={false} controlId={'companyType'} title={'Typ'}
                            className={'pb-2'}>
                            <GenericInput type={'select'} name={'companyType'} {...commonProps} disabled={!checkRole(Role.SUPERADMIN)}>
                                <option value={CompanyType.COMPANY}>Företag/Dotterbolag</option>
                                <option value={CompanyType.PARENT}>Moderbolag</option>
                            </GenericInput>
                        </FormBase>
                    </Col>
                </Row>
            </Hr>
            <When condition={updated.companyType === CompanyType.COMPANY}>
                <Hr title={"Koncerninfo"}>
                    <Row>
                        <Col md={6}>
                            <FormBase row={false} controlId={'parentCompanyId'} title={'Moderbolag'}
                                className={'pb-2'}>
                                <GenericInput type={'select'} name={'parentCompanyId'} {...commonProps} disabled={!checkRole(Role.SUPERADMIN)}>
                                    {[
                                        <option value={''} key={''}>Ej angivet</option>,
                                        ...companyParents.map((v, index) => <option value={v.id} key={v.id}>{v.name}</option>)
                                        
                                    ]}
                                </GenericInput>
                            </FormBase>
                        </Col>
                        {/*    <Col md={6}>*/}
                        {/*        <FormBase row={false} controlId={'region'} title={'Region/Grupp'}*/}
                        {/*            className={'pb-2'}>*/}
                        {/*            <GenericInput type={'select'} name={'region'} {...commonProps}>*/}
                        {/*            </GenericInput>*/}
                        {/*        </FormBase>*/}
                        {/*    </Col>*/}
                    </Row>
                </Hr>
            </When>
        </ModalForm>
    )
}


export default NewCompanyModal